import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { stub as $t } from '@nbcnews/analytics-framework';

import {
  filterItems,
  getInitialFilters,
  getPackageItemsTaxonomy,
  getUpdatedCategoryValues,
  applyUrlFilters,
  getNewActiveFilters,
  generatePageUrl,
} from 'lib/waffleUtils';

import { ErrorBoundary } from 'components/ErrorBoundary';
import { Header } from 'components/packages/Waffle/Header';
import { List } from 'components/packages/Waffle/List';
import { Pagination } from 'components/packages/Waffle/Pagination';
import { LayoutContext } from 'lib/ContextTypes/layout';

import { getAmazonProductOffersTrackingObject } from 'lib/commerceUtils';
import { ProductWaffleCard } from './Card';

import styles from './styles.module.scss';

$t('register', 'rmn_amazon_products_known', { allowDuplicate: true });

const mapStateToProps = ({ shared, navbar, front }) => ({
  vertical: shared.vertical,
  navTheme: navbar.theme,
  draftId: front.draftId,
});

/**
 * BaseProductWaffle component renders the main product waffle UI, including filters, product cards, and pagination.
 *
 * @param {object} props - Component props.
 * @param {object} props.content - The content data for the waffle, including items, metadata, and pagination.
 * @param {string} props.pkgClassName - Additional class name(s) for the waffle container.
 * @param {string} props.draftId - Optional draft ID for previewing draft content.
 * @returns {JSX.Element} The rendered product waffle component.
 */
const BaseProductWaffle = ({
  content,
  pkgClassName = 'pkg product-waffle',
  draftId,
}) => {
  const {
    metadata,
    pagination,
    context = {},
  } = content || {};

  const {
    multiSelect: enableMultiselectFilters = true,
    priceFilter,
    customFilters,
  } = metadata || {};

  const initialFilters = useMemo(() => {
    const taxonomyFilters = customFilters?.map((f) => ({
      ...f,
      type: 'taxonomy',
    }));

    const pxFilter = priceFilter?.length ? { type: 'price', ...priceFilter[0] } : null;

    return getInitialFilters(taxonomyFilters, pxFilter);
  }, [priceFilter, customFilters]);


  const { pageRoute, vertical } = useContext(LayoutContext);
  const containerRef = useRef(null);
  const [categories, setCategories] = useState(initialFilters.categories);
  const [filterValues, setFilterValues] = useState(initialFilters.filterValues);
  const [categoryToDisplay, setCategoryToDisplay] = useState(initialFilters.categoryToDisplay);
  const [activeFilters, setActiveFilters] = useState(new Map());
  const [idToTaxonomy, setIdToTaxonomy] = useState({});
  const [displayNameByKey, setDisplayNameByKey] = useState(initialFilters.displayNameByKey);

  useEffect(() => {
    getPackageItemsTaxonomy(vertical, pageRoute, draftId)
      .then((taxonomy) => setIdToTaxonomy(taxonomy))
      .catch(console.error);
  }, [vertical, pageRoute, draftId]);

  const waffleCards = (content?.items || []).filter((item) => item);
  const taxonomyFilterStates = Object.entries(filterValues).map(([key, filterValue]) => ({
    type: key === 'priceFilter' ? 'price' : 'taxonomy',
    values: filterValue,
  }));
  const filteredCards = filterItems(
    waffleCards,
    taxonomyFilterStates,
    idToTaxonomy,
  );

  const items = activeFilters.size > 0 ? filteredCards : waffleCards;
  const countWithoutAds = items.filter(
    ({ isCustomAd }) => !isCustomAd,
  ).length;
  /**
  * Builds the URL for a specific page.
  *
  * @param {number} page - The page number to generate the URL for.
  * @returns {string} The page url with updated filters.
  */
  const buildPageUrl = (page) => (
    generatePageUrl(context.routePath, page, pagination.urlFilter)
  );

  /**
  * Navigates to a specific page by updating the window location.
  *
  * @param {number|string} page - The page number or identifier to navigate to.
  */
  const navigatePageCallback = (page) => {
    window.location.href = generatePageUrl(context.routePath, page, pagination.urlFilter);
  };

  const clearAllFilters = useCallback(() => {
    if (pagination) {
      window.location.href = generatePageUrl(context.routePath, 1);
      return;
    }

    setActiveFilters(new Map());
    setFilterValues(initialFilters.filterValues);
    setCategories(initialFilters.categories);
    setCategoryToDisplay(initialFilters.categoryToDisplay);
    setDisplayNameByKey(initialFilters.displayNameByKey);
  }, [initialFilters, context.routePath]);

  const handleFilterChange = useCallback((category) => (key, value) => {
    const newCategoryValues = getUpdatedCategoryValues(
      key,
      value,
      filterValues,
      category,
      enableMultiselectFilters,
    );

    // update filter values
    const newFilterValues = {
      ...filterValues,
      [category]: newCategoryValues,
    };

    const newActiveFilters = getNewActiveFilters({
      activeFilters,
      hasMultiSelect: enableMultiselectFilters,
      key,
      value,
      category,
      displayNameByKey,
      newCategoryValues,
    });

    if (pagination) {
      // for paged waffles we go back to page one when changing filters
      // because we don't know how many results there will be
      window.location.href = generatePageUrl(context.routePath, 1, newFilterValues);
      return;
    }

    // get the updated waffles
    setActiveFilters(newActiveFilters);
    setCategories({ ...categories });
    setFilterValues(newFilterValues);
  }, [
    pagination,
    context.routePath,
    filterValues,
    activeFilters,
    categories,
    enableMultiselectFilters,
    displayNameByKey,
  ]);

  useEffect(() => {
    const amazonProducts = content?.items?.filter((item) => item?.type === 'product')?.map((contentItem) => contentItem.item) || [];
    const amazonProductOffersTrackingObject = getAmazonProductOffersTrackingObject(amazonProducts);

    if (amazonProductOffersTrackingObject) {
      $t('track', 'rmn_amazon_products_known', amazonProductOffersTrackingObject);
    }
  }, [vertical, pageRoute, draftId, content]);

  useEffect(() => {
    if (!pagination?.urlFilter) {
      return;
    }

    const paginatedActiveFilters = new Map();
    const finalFilters = applyUrlFilters(initialFilters.filterValues, pagination.urlFilter);

    Object.entries(finalFilters).forEach(([key, value]) => {
      Object.entries(value).forEach(([k, v]) => {
        if (v) {
          paginatedActiveFilters.set(k, {
            key: k,
            category: key,
            displayName: initialFilters.displayNameByKey.get(k),
          });
        }
      });
    });

    setActiveFilters(paginatedActiveFilters);
    setFilterValues(finalFilters);
    setCategories(initialFilters.categories);
    setCategoryToDisplay(initialFilters.categoryToDisplay);
    setDisplayNameByKey(initialFilters.displayNameByKey);
  }, [initialFilters, pagination]);

  return (
    <div
      className={classNames(pkgClassName, styles.productWaffle)}
      ref={containerRef}
      data-packageid={content?.id}
      data-testid="product-waffle"
      data-ready={Object.keys(idToTaxonomy || {}).length > 0}
    >
      <Header
        activeFilters={Array.from(activeFilters.values())}
        cardCount={pagination?.totalCount || countWithoutAds}
        categories={categories}
        categoryToDisplay={categoryToDisplay}
        clearFilter={clearAllFilters}
        content={content}
        enableMultiselectFilters={enableMultiselectFilters}
        filterDisplayNameByKey={displayNameByKey}
        filterOnChange={handleFilterChange}
        filterValues={filterValues}
        isFilterActive={activeFilters.size > 0}
        loading={false}
        showDisclaimer
      />
      {items.length > 0 && (
        <List
          adsDisabled
          cards={items}
          cardRenderer={(card) => (card && (
            <ErrorBoundary key={card.id}>
              <ProductWaffleCard card={card} />
            </ErrorBoundary>
          ))}
          mobileDisplayColumns
          isProductWaffle
          listClasses={styles.productWaffleList}
        />
      )}
      {pagination ? (
        <Pagination
          pagination={pagination}
          navigatePageCallback={navigatePageCallback}
          generatePageUrl={buildPageUrl}
          usePageLink
        />
      ) : null}
    </div>
  );
};

export const ProductWaffle = connect(mapStateToProps)(BaseProductWaffle);
